import { parseJsx, walk } from './parse';

const CDN = 'https://esm.sh';
const PREACT = 'preact@10.29.8';

/**
 * `deps=` e não `external=`: os dois evitam um segundo preact dentro do lucide
 * (que quebraria os hooks), mas `external` deixa `import ... from "preact"` cru
 * no módulo, contando com um import map. Não há import map — o bloco roda em
 * shadow root, no documento do editor. `deps` resolve para a URL da MESMA versão.
 */
export const PINS: Record<string, string> = {
	preact: `${CDN}/${PREACT}`,
	motion: `${CDN}/motion@12.43.0`,
	gsap: `${CDN}/gsap@3.15.0`,
	three: `${CDN}/three@0.185.1`,
	'lucide-preact': `${CDN}/lucide-preact@1.28.0?deps=${PREACT}`,
};

const ICONE = /^lucide-preact\/icons\/([a-z0-9-]+)(\.js)?$/;
const LUCIDE = `${CDN}/lucide-preact@1.28.0/dist/esm/icons`;

/** Builtins do Node: não existem no navegador, ponto. */
const NODE_BUILTINS = new Set([
	'fs', 'path', 'os', 'crypto', 'http', 'https', 'net', 'child_process', 'stream',
	'zlib', 'util', 'events', 'buffer', 'url', 'querystring', 'worker_threads', 'process',
]);

export interface Rejeitado {
	spec: string;
	motivo: string;
}

/** O pacote de um especificador: "three/addons/x.js" → "three", "@scope/a/b" → "@scope/a". */
export function packageOf(spec: string): string {
	const partes = spec.split('/');
	return spec.startsWith('@') ? partes.slice(0, 2).join('/') : partes[0];
}

interface Especificador {
	start: number;
	end: number;
	spec: string;
}

/**
 * O literal de cada módulo que o arquivo puxa: import, `export ... from` e
 * `import()`. O dinâmico é `CallExpression` com callee `Import` — não existe nó
 * `ImportExpression` no babel, e procurar por ele era um ramo que nunca rodava.
 */
function especificadores(src: string): Especificador[] {
	const out: Especificador[] = [];

	const anota = (no: unknown) => {
		const s = no as { type?: unknown; start?: unknown; end?: unknown; value?: unknown } | null;
		if (!s || s.type !== 'StringLiteral') return;
		if (typeof s.start !== 'number' || typeof s.end !== 'number' || typeof s.value !== 'string') return;
		out.push({ start: s.start, end: s.end, spec: s.value });
	};

	walk(parseJsx(src), (n) => {
		if (n.type === 'ImportDeclaration' || n.type === 'ExportNamedDeclaration' || n.type === 'ExportAllDeclaration') {
			anota(n.source);
			return;
		}
		if (n.type === 'CallExpression' && (n.callee as { type?: unknown } | null)?.type === 'Import') {
			anota((n.arguments as unknown[] | undefined)?.[0]);
		}
	});

	return out.sort((a, b) => a.start - b.start);
}

/** Todo módulo que o arquivo puxa: import, export ... from e import() literal. */
export function importsOf(src: string): string[] {
	return [...new Set(especificadores(src).map((e) => e.spec))];
}

export function rejectedImports(src: string): Rejeitado[] {
	const out: Rejeitado[] = [];

	for (const spec of importsOf(src)) {
		if (spec.startsWith('.') || spec.startsWith('/')) {
			out.push({ spec, motivo: 'o bloco é um arquivo só — não existe caminho relativo' });
			continue;
		}
		const pacote = packageOf(spec.replace(/^node:/, ''));
		if (spec.startsWith('node:') || NODE_BUILTINS.has(pacote)) {
			out.push({ spec, motivo: 'é do Node, não existe no navegador' });
			continue;
		}
		if (pacote === 'react' || pacote === 'react-dom') {
			out.push({ spec, motivo: 'o bloco é Preact — use preact/hooks' });
		}
	}

	walk(parseJsx(src), (n) => {
		if (n.type !== 'ImportDeclaration') return;
		const spec = (n.source as { value?: unknown } | null)?.value;
		if (typeof spec !== 'string' || !ICONE.test(spec)) return;

		const nomeados = ((n.specifiers ?? []) as Array<{ type?: string }>).filter((s) => s.type === 'ImportSpecifier');
		if (nomeados.length) {
			const nome = spec.replace(ICONE, '$1').replace(/(^|-)(\w)/g, (_, __, c: string) => c.toUpperCase());
			out.push({ spec, motivo: `ícone do lucide é export default — use: import ${nome} from '${spec}'` });
		}
	});

	return out;
}

/** "ArrowUpRight" → "arrow-up-right", "Volume2" → "volume-2": o módulo do ícone. */
function kebab(nome: string): string {
	return nome
		.replace(/([a-z0-9])([A-Z])/g, '$1-$2')
		.replace(/([A-Za-z])(\d)/g, '$1-$2')
		.toLowerCase();
}

/** Nome de componente: `<ArrowUpRight />` é candidato, `<div>` e `<Icon.Foo>` não. */
const PASCAL = /^[A-Z][A-Za-z0-9]*$/;

/** Todo nome que o arquivo LIGA a alguma coisa — import, declaração, parâmetro. */
function declarados(ast: unknown): Set<string> {
	const nomes = new Set<string>();
	// varre o padrão inteiro: `const { a: b } = x` e `([{ c }]) => …` também ligam
	const liga = (n: unknown) => walk(n, (x) => {
		if (x.type === 'Identifier' && typeof x.name === 'string') nomes.add(x.name);
	});

	walk(ast, (n) => {
		switch (n.type) {
			case 'ImportDefaultSpecifier':
			case 'ImportSpecifier':
			case 'ImportNamespaceSpecifier':
				liga(n.local);
				break;
			case 'FunctionDeclaration':
			case 'ClassDeclaration':
				liga(n.id);
				liga(n.params);
				break;
			case 'FunctionExpression':
			case 'ArrowFunctionExpression':
				liga(n.params);
				break;
			case 'VariableDeclarator':
				liga(n.id);
				break;
		}
	});
	return nomes;
}

/** Todo nome que o arquivo liga, do jeito que o parser vê — nunca por texto. */
export function nomesLigados(src: string): Set<string> {
	try {
		return declarados(parseJsx(src));
	} catch {
		return new Set();
	}
}

/** Componentes usados no JSX que ninguém declarou — na prática, ícone esquecido. */
export function iconesFaltando(src: string): string[] {
	let ast: unknown;
	try {
		ast = parseJsx(src);
	} catch {
		return [];
	}

	const ligados = declarados(ast);
	const faltando = new Set<string>();
	walk(ast, (n) => {
		if (n.type !== 'JSXElement') return;
		const nome = (n.openingElement as { name?: { type?: string; name?: unknown } } | null)?.name;
		if (nome?.type !== 'JSXIdentifier' || typeof nome.name !== 'string') return;
		if (PASCAL.test(nome.name) && !ligados.has(nome.name)) faltando.add(nome.name);
	});

	return [...faltando].sort();
}

export function corrigeIcones(src: string): string {
	const faltando = iconesFaltando(src);
	if (!faltando.length) return src;

	const linhas = faltando.map((n) => `import ${n} from 'lucide-preact/icons/${kebab(n)}';`);
	return `${linhas.join('\n')}\n${src}`;
}

const HOOKS = new Set([
	'useState', 'useEffect', 'useLayoutEffect', 'useRef', 'useMemo', 'useCallback',
	'useReducer', 'useContext', 'useId', 'useErrorBoundary', 'useDebugValue',
]);

/** Hooks chamados que ninguém declarou nem importou — o esquecimento gêmeo do ícone. */
export function hooksFaltando(src: string): string[] {
	let ast: unknown;
	try {
		ast = parseJsx(src);
	} catch {
		return [];
	}

	const ligados = declarados(ast);
	const faltando = new Set<string>();
	walk(ast, (n) => {
		if (n.type !== 'CallExpression') return;
		const callee = n.callee as { type?: string; name?: unknown } | null;
		if (callee?.type !== 'Identifier' || typeof callee.name !== 'string') return;
		if (HOOKS.has(callee.name) && !ligados.has(callee.name)) faltando.add(callee.name);
	});

	return [...faltando].sort();
}

export function corrigeHooks(src: string): string {
	const faltando = hooksFaltando(src);
	if (!faltando.length) return src;

	return `import { ${faltando.join(', ')} } from 'preact/hooks';\n${src}`;
}

/** Os dois consertos de import que o modelo esquece, na ordem em que se somam. */
export function corrigeImports(src: string): string {
	return corrigeHooks(corrigeIcones(src));
}

const GENERICO = 'circle';

/** Plural/singular é o erro que aparece; o resto cai no genérico. */
function variacoes(nome: string): string[] {
	return nome.endsWith('s') ? [nome.slice(0, -1)] : [`${nome}s`];
}

async function existeNaCDN(spec: string): Promise<boolean> {
	try {
		return (await fetch(resolveImport(spec), { method: 'HEAD' })).ok;
	} catch {
		return false; // sem rede, o genérico é mais seguro que um 404 fatal
	}
}

export interface TrocaDeIcone {
	de: string;
	para: string;
}

export async function confereIcones(src: string, existe = existeNaCDN): Promise<{ code: string; trocas: TrocaDeIcone[] }> {
	const specs = importsOf(src).filter((s) => ICONE.test(s));
	if (!specs.length) return { code: src, trocas: [] };

	const trocas: TrocaDeIcone[] = [];
	// em paralelo: bloco tem 2-5 ícones e a CDN responde em ~50ms cada
	const veredictos = await Promise.all(
		specs.map(async (spec) => {
			if (await existe(spec)) return null;

			const nome = ICONE.exec(spec)![1];
			for (const alt of variacoes(nome)) {
				const candidato = `lucide-preact/icons/${alt}`;
				if (await existe(candidato)) return { spec, candidato };
			}
			return { spec, candidato: `lucide-preact/icons/${GENERICO}` };
		}),
	);

	let code = src;
	for (const v of veredictos) {
		if (!v) continue;
		code = code.split(`'${v.spec}'`).join(`'${v.candidato}'`).split(`"${v.spec}"`).join(`"${v.candidato}"`);
		trocas.push({ de: v.spec, para: v.candidato });
	}

	return { code, trocas };
}

export interface IconeLocal {
	/** o identificador do componente; ele NÃO muda na troca */
	nome: string;
	/** o nome em kebab que o modelo apostou no lucide */
	lucide: string;
	/** a declaração inteira, para virar import */
	start: number;
	end: number;
	/** o `data-lucide`, que some quando o SVG fica */
	dicaStart: number;
	dicaEnd: number;
}

const DICA = 'data-lucide';

interface Faixa {
	start: number;
	end: number;
}

function faixa(no: unknown): Faixa {
	const n = (no ?? {}) as { start?: unknown; end?: unknown };
	return { start: typeof n.start === 'number' ? n.start : 0, end: typeof n.end === 'number' ? n.end : 0 };
}

/** O primeiro elemento JSX dentro de um nó — a raiz do que o componente devolve. */
function primeiroJsx(no: unknown): Record<string, unknown> | null {
	const achados: Array<Record<string, unknown>> = [];
	walk(no, (n) => {
		if (n.type === 'JSXElement') achados.push(n as Record<string, unknown>);
	});
	return achados.length ? achados.sort((a, b) => faixa(a).start - faixa(b).start)[0] : null;
}

/** O atributo, com a faixa que o inclui — para removê-lo sem deixar espaço duplo. */
function atributoJsx(el: Record<string, unknown>, nome: string, src: string): { valor: string; start: number; end: number } | null {
	const open = el.openingElement as Record<string, unknown> | null;
	const attrs = ((open?.attributes ?? []) as Array<Record<string, unknown>>).filter((a) => a.type === 'JSXAttribute');

	for (const a of attrs) {
		const id = a.name as { type?: string; name?: unknown } | null;
		if (id?.type !== 'JSXIdentifier' || id.name !== nome) continue;

		const v = a.value as { type?: string; value?: unknown } | null;
		if (v?.type !== 'StringLiteral' || typeof v.value !== 'string') return null;

		let start = faixa(a).start;
		while (start > 0 && /\s/.test(src[start - 1])) start--;
		return { valor: v.value, start, end: faixa(a).end };
	}
	return null;
}

export function iconesLocais(src: string): IconeLocal[] {
	let ast: unknown;
	try {
		ast = parseJsx(src);
	} catch {
		return [];
	}

	const exportados: Faixa[] = [];
	walk(ast, (n) => {
		if (n.type === 'ExportDefaultDeclaration' || n.type === 'ExportNamedDeclaration') exportados.push(faixa(n));
	});
	const exportado = (f: Faixa) => exportados.some((e) => f.start >= e.start && f.end <= e.end);

	const out: IconeLocal[] = [];
	const anota = (nome: unknown, corpo: unknown, f: Faixa) => {
		if (typeof nome !== 'string' || !PASCAL.test(nome) || exportado(f)) return;

		const raiz = primeiroJsx(corpo);
		const open = raiz?.openingElement as { name?: { name?: unknown } } | null;
		if (open?.name?.name !== 'svg') return;

		const dica = atributoJsx(raiz as Record<string, unknown>, DICA, src);
		if (!dica?.valor.trim()) return;

		out.push({ nome, lucide: dica.valor.trim(), ...f, dicaStart: dica.start, dicaEnd: dica.end });
	};

	walk(ast, (n) => {
		if (n.type === 'FunctionDeclaration') {
			anota((n.id as { name?: unknown } | null)?.name, n.body, faixa(n));
			return;
		}
		if (n.type !== 'VariableDeclaration') return;

		const decls = (n.declarations ?? []) as Array<Record<string, unknown>>;
		if (decls.length !== 1) return; // `const A = …, B = …` não dá para recortar sozinho

		const init = decls[0].init as { type?: string; body?: unknown } | null;
		if (init?.type !== 'ArrowFunctionExpression' && init?.type !== 'FunctionExpression') return;
		anota((decls[0].id as { name?: unknown } | null)?.name, init.body, faixa(n));
	});

	return out.sort((a, b) => a.start - b.start);
}

export async function resolveIcones(src: string, existe = existeNaCDN): Promise<{ code: string; trocas: TrocaDeIcone[] }> {
	const locais = iconesLocais(src);
	if (!locais.length) return { code: src, trocas: [] };

	const veredictos = await Promise.all(
		locais.map(async (ic) => ({ ic, temNoLucide: await existe(`lucide-preact/icons/${ic.lucide}`) })),
	);

	const trocas: TrocaDeIcone[] = [];
	const imports: string[] = [];
	let code = src;

	// de trás para frente: os offsets ainda não usados continuam válidos
	for (const { ic, temNoLucide } of [...veredictos].reverse()) {
		if (!temNoLucide) {
			code = code.slice(0, ic.dicaStart) + code.slice(ic.dicaEnd);
			continue;
		}

		const spec = `lucide-preact/icons/${ic.lucide}`;
		imports.push(`import ${ic.nome} from '${spec}';`);
		trocas.push({ de: ic.nome, para: spec });

		let fim = ic.end;
		while (fim < code.length && /[ \t]/.test(code[fim])) fim++;
		if (code[fim] === '\n') fim++;
		code = code.slice(0, ic.start) + code.slice(fim);
	}

	return {
		code: imports.length ? `${imports.reverse().join('\n')}\n${code}` : code,
		trocas: trocas.reverse(),
	};
}

/** A URL de um especificador: ícone, pinado, subpath de pinado, ou último do npm. */
export function resolveImport(spec: string): string {
	const icone = ICONE.exec(spec);
	if (icone) return `${LUCIDE}/${icone[1]}.js?deps=${PREACT}`;

	const pin = PINS[spec];
	if (pin) return pin;

	const pacote = packageOf(spec);
	const pinDoPai = PINS[pacote];
	if (pinDoPai) return pinDoPai + spec.slice(pacote.length);

	return `${CDN}/${spec}`;
}

/**
 * Troca todo especificador por URL completa. É o que substitui o import map:
 * ele é por documento, e o editor é um app que já carregou módulos — injetar um
 * depois disso dá "An import map is added after module script load was triggered".
 *
 * O que `rejectedImports` barrou fica como está, para falhar alto em vez de virar
 * uma URL que não existe.
 */
export function reescreveImports(src: string): string {
	const rejeitados = new Set(rejectedImports(src).map((r) => r.spec));

	let out = src;
	// de trás para frente: os offsets ainda não usados continuam válidos
	for (const { start, end, spec } of especificadores(src).reverse()) {
		if (rejeitados.has(spec)) continue;
		out = `${out.slice(0, start)}'${resolveImport(spec)}'${out.slice(end)}`;
	}

	return out;
}

/** Os pacotes que o bloco puxa de fora — o que o editor mostra para você. */
export function packagesOf(src: string): string[] {
	const rejeitados = new Set(rejectedImports(src).map((r) => r.spec));
	const pacotes = new Set<string>();

	for (const spec of importsOf(src)) {
		if (rejeitados.has(spec)) continue;
		const pacote = packageOf(spec);
		if (pacote !== 'preact') pacotes.add(pacote);
	}

	return [...pacotes].sort();
}

/** "https://esm.sh/canvas-confetti" → "canvas-confetti", para a mensagem de erro. */
export function packageFromUrl(url: string): string | null {
	const m = new RegExp(`${CDN}/([^?\\s"']+)`).exec(url);
	return m ? packageOf(m[1].replace(/^v\d+\//, '')) : null;
}
